import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'

import { handleException } from 'clients/exception-handling'
import type { FilterState } from 'features/filters/store'
import { useFilterStore } from 'features/filters/store'
import { usePanelStore } from 'features/panels/store'
import { useProjectStore } from 'features/project/store'
import { useSavedViewService } from 'features/saved-views/service'
import { SavedView, SavedViewPayload } from 'features/saved-views/types'
import { buildProjectPageUrl } from 'routes/builders'
import type { HashNavigationState } from 'routes/navigation-state'
import { ProjectState } from 'types/project'

export function useSavedViews(): {
  updateFilters: (view: SavedView) => void
  deleteView: (view_uuid: string) => Promise<void>
  saveView: (view: SavedViewPayload) => Promise<void>
} {
  const savedViewService = useSavedViewService()
  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const setHasLoadedFilters = useFilterStore(
    (state: FilterState) => state.setHasLoadedFilters,
  )

  /**
   * Load a saved view by navigating to its stored URL.
   *
   * The saved view stores:
   *   - page: 'feedback', 'dashboard', etc.
   *   - search_url: '?filters={hash}' (old DB format, or could also contain
   *     other params for non-filter pages)
   *
   * We extract the hash from search_url and build the new path-based URL
   * directly (e.g. /project/{uuid}/feedback/{hash}) instead of relying on
   * the backward-compat redirect.
   *
   * hasLoadedFilters is set to false to block downstream queries until the
   * new filter state is fully hydrated from the hash.
   *
   * Panel state is reset first so the saved hash is authoritative on load.
   * The shared URL->State hydration only applies the saved view's filters when
   * the saved view's panel count is >= the current in-memory comparative panel
   * count (see usePageFilterHash). That panel count persists across in-project
   * navigation and is only reset when the user returns to the projects list, so
   * loading a saved view while a larger comparative layout lingers would
   * otherwise drop the saved filters and keep the frontend's. Resetting panels
   * to a single-panel baseline makes currentPanelCount = 1, so any saved view's
   * filters always apply (and hydration then restores the saved view's own
   * panel count/comparative flags).
   */
  const updateFilters = (view: SavedView): void => {
    const viewUrl = view.page
    const searchUrl = view.search_url
    if (viewUrl) {
      usePanelStore.getState().resetPanels()
      setHasLoadedFilters(false)

      // Extract hash from stored search_url and navigate to path-based format
      const params = new URLSearchParams(searchUrl)
      const hash = params.get('filters')
      const url = hash
        ? buildProjectPageUrl(projectId, viewUrl, hash)
        : `/project/${projectId}/${viewUrl}${searchUrl}`
      navigate(url, {
        state: { hashAuthority: 'url' } satisfies HashNavigationState,
      })
    }
  }

  const deleteView = async (view_uuid: string): Promise<void> => {
    try {
      await savedViewService.deleteSavedView(projectId, view_uuid)
      await queryClient.resetQueries(['saved-views', projectId])
      toast.success('Filter deleted')
    } catch (error) {
      handleException(error, { operation: 'saved-view.delete' })

      toast.error(
        (
          error as Error & {
            response?: { data?: { detail?: { msg?: string } } }
          }
        )?.response?.data?.detail?.msg || 'Error deleting view.',
      )
    }
  }

  const saveSavedView = async (view: SavedViewPayload): Promise<void> => {
    try {
      await savedViewService.saveView(projectId, view)
      await queryClient.refetchQueries(['saved-views', projectId])
      toast.success('View saved')
    } catch (error) {
      handleException(error, { operation: 'saved-view.save' })

      toast.error(
        (
          error as Error & {
            response?: { data?: { detail?: { msg?: string } } }
          }
        )?.response?.data?.detail?.msg || 'Error saving view.',
      )
    }
  }

  return {
    updateFilters,
    deleteView,
    saveView: saveSavedView,
  }
}
